import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import {
  DownOutlined,
  UpOutlined,
  SafetyCertificateOutlined,
  LoadingOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { SubredditGuidelinesResponse } from '../../../services/aiReplyService';

const { Text } = Typography;

const T = {
  brand: '#FF4500',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  border: '#E5E7EB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
  textMuted: '#898A8F',
};

interface CommunityGuidelinesPanelProps {
  subreddit: string;
  guidelines: SubredditGuidelinesResponse | null;
  loading: boolean;
  error: string | null;
  expanded: boolean;
  onToggle: () => void;
}

const CommunityGuidelinesPanel: React.FC<CommunityGuidelinesPanelProps> = ({
  subreddit,
  guidelines,
  loading,
  error,
  expanded,
  onToggle,
}) => {
  // Determine status icon + color (subtle, no big colored alert blocks)
  const status: {
    icon: React.ReactNode;
    color: string;
    bg: string;
    border: string;
    label: string;
  } = (() => {
    if (loading) {
      return {
        icon: <LoadingOutlined />,
        color: T.textSecondary,
        bg: T.bgMuted,
        border: T.border,
        label: `Loading r/${subreddit} guidelines…`,
      };
    }
    if (error) {
      return {
        icon: <WarningOutlined />,
        color: '#B45309',
        bg: '#FFFBEB',
        border: '#FDE68A',
        label: `Couldn't load r/${subreddit} guidelines — replies will use general best practices`,
      };
    }
    if (guidelines && guidelines.available) {
      return {
        icon: <SafetyCertificateOutlined />,
        color: '#047857',
        bg: '#ECFDF5',
        border: '#A7F3D0',
        label: `Following r/${subreddit} guidelines (${guidelines.rule_count} rule${
          guidelines.rule_count === 1 ? '' : 's'
        })${guidelines.restricts_promotion ? ' — self-promotion restricted' : ''}`,
      };
    }
    return {
      icon: <InfoCircleOutlined />,
      color: T.textSecondary,
      bg: T.bgMuted,
      border: T.border,
      label: `r/${subreddit} has no posted rules — using general best practices`,
    };
  })();

  return (
    <div>
      <div
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: status.bg,
          border: `1px solid ${status.border}`,
          borderRadius: 6,
          transition: 'background 0.12s ease',
        }}
      >
        <span style={{ color: status.color, fontSize: 12, display: 'inline-flex' }}>
          {status.icon}
        </span>
        <Text style={{ flex: 1, fontSize: 12, color: T.text, fontWeight: 500 }}>
          {status.label}
        </Text>
        <Button
          type="text"
          size="small"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          style={{
            padding: '0 6px',
            height: 22,
            color: T.textSecondary,
            fontSize: 11,
          }}
        >
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </div>
      {expanded && guidelines && guidelines.available && guidelines.rules.length > 0 && (
        <div
          style={{
            marginTop: 6,
            padding: '8px 10px',
            background: T.bg,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
          }}
        >
          <Space size={[6, 6]} wrap>
            {guidelines.rules.slice(0, 6).map((rule, i) => (
              <Tag
                key={i}
                style={{
                  margin: 0,
                  fontSize: 11,
                  padding: '0 6px',
                  lineHeight: '20px',
                  background: T.bgMuted,
                  color: T.textSecondary,
                  border: `1px solid ${T.border}`,
                }}
              >
                {rule.short_name || `Rule ${i + 1}`}
              </Tag>
            ))}
            {guidelines.rules.length > 6 && (
              <Tag
                style={{
                  margin: 0,
                  fontSize: 11,
                  padding: '0 6px',
                  lineHeight: '20px',
                  background: T.bgMuted,
                  color: T.textMuted,
                  border: `1px solid ${T.border}`,
                }}
              >
                +{guidelines.rules.length - 6} more
              </Tag>
            )}
          </Space>
        </div>
      )}
    </div>
  );
};

export default CommunityGuidelinesPanel;
